/* global window, React, Icon, Button, Modal, toast, useState, useMemo, useEffect, useRef */
// ============================================================
// AI Regnskapsfører — arbeidsflater i modal
// ============================================================
// Raden på AI-siden er en status, ikke en arbeidsflate. Skulle brukeren gjøre
// selve jobben, ble hen sendt til Bank eller Innboks og mistet oversikten hen
// nettopp åpnet. Derfor monteres arbeidsflaten i en modal over AI-siden.
//
// Det som monteres er *arbeidsflaten*, ikke siden. En hel side i en modal
// drar med seg visningsbytte, papirkurv, opplasting og e-postmottak — alt
// sammen feil når du står i en godkjenning — og gir tre lag med modaler.
// Komponentene under (`BankReconcileMode`, `ReceiptInboxWorklist`) er den
// avgrensede arbeidsflaten, og brukes av både siden og modalen.
//
// Konsistensen mellom Bank og Innboks ligger i skallet: samme tittel, samme
// telleverk, samme fotgrammatikk. Innmaten er to forskjellige arbeidsflater,
// og skal få lov til å se forskjellig ut.

// Felles skall. Bredden er arbeidsflatens, ikke dialogens — en avstemming med
// bilagspanel ved siden av trenger plass, og `.modal` kapper høyden selv.
function ApSurfaceModal({ title, subtitle, onClose, footer, children, max = 1240, className = '' }) {
  // Arbeidsflaten kan vaere bred (avstemming med bilagspanel). Utvid-knappen ved
  // siden av lukkekrysset lar brukeren ta hele skjermen, og slaas av igjen.
  const [full, setFull] = useState(false);
  return <Modal open onClose={onClose} title={title} max={max} portal
    className={className + (full ? ' ap-surface-modal-full' : '')}
    titleRight={<React.Fragment>
      {subtitle ? <span className="t-sm muted" style={{ fontWeight: 700 }}>{subtitle}</span> : null}
      <window.IconButton name={full ? 'shrink' : 'expand'} size="sm" variant="ghost"
        aria-pressed={full}
        title={full ? t('ap.surface.exitFullscreen', 'Avslutt fullskjerm') : t('ap.surface.fullscreen', 'Vis i fullskjerm')}
        aria-label={full ? t('ap.surface.exitFullscreen', 'Avslutt fullskjerm') : t('ap.surface.fullscreen', 'Vis i fullskjerm')}
        onClick={() => setFull((current) => !current)} />
    </React.Fragment>}
    footer={footer}>
    <div className="ap-surface">{children}</div>
  </Modal>;
}

// Utskriften som overlapper måneden agenten står i. Vi bruker hele perioden,
// siden en kontoutskrift kan starte før den første og likevel dekke måneden.
const apStatementInMonth = (statement, month, year) => {
  return window.apMonthControlStatementCovers(statement, month, year);
};

const AP_SURFACE_MONTH_NAMES = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const apMonthControlPeriodLabel = (month, year) => month == null
  ? String(year || '')
  : `${AP_SURFACE_MONTH_NAMES[Number(month)] || ''} ${year || ''}`.trim();

const apStatementSettled = (statement) => {
  const status = String(statement?.status || '').toLowerCase();
  return Boolean(statement?.bookedAt || statement?.reconciledAt || ['archived', 'reconciled', 'booked'].includes(status));
};

function ApBankDoneSurface({ stmt, acctLabel, onReopen }) {
  const lines = stmt?.lineItems || [];
  return <div className="bg-shell"><div className="bg-done">
    <span className="bg-done-icon"><Icon name="check" size={30}/></span>
    <div><span className="pp pp-paid">{t('cust.archived')}</span><h1>{t('bg.reconciliationDone')}</h1><p>{t('bg.reconciliationDoneHelp')}</p></div>
    <div className="bg-recap">
      <div><span>{t('ui.period')}</span><b>{stmt?.month || ''}</b></div>
      <div><span>{t('bank.bankAccount')}</span><b>{String(acctLabel || 'Driftskonto')}</b></div>
      <div><span>{t('bg.bankLines')}</span><b>{lines.length}</b></div>
      <div><span data-help={t('helpTopics.contactStatus.body')} data-help-title={t('helpTopics.contactStatus.title')}>{t('cust.status')}</span><b className="ok">{t('bg.reconciled')}</b></div>
    </div>
    <div className="row gap-10 wrap"><Button icon="refresh" onClick={onReopen} data-help={t('helpTopics.reopen.body')} data-help-title={t('helpTopics.reopen.title')}>{t('bg.reopenReconciliation')}</Button></div>
  </div></div>;
}

// Kontoen finnes selv om utskriften mangler. Fanen skal derfor staa der uansett,
// og mangelen loses der den oppstaar — med opplasting i modalen, ikke med en
// tur innom Bank-siden.
function ApBankStatementUploadSurface({ accountLabel, accountId, periodLabel, onUploaded, children }) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const upload = async (file) => {
    if (!file || busy) return;
    if (!window.importBankStatementFile) { toast(t('ap.surface.bankUploadUnavailable', 'Opplasting av bankutskrift er ikke tilgjengelig ennå.'), 'error'); return; }
    setBusy(true);
    try {
      const stmt = await window.importBankStatementFile({ file, bankAccountId: accountId, source: 'drift' });
      if (stmt) onUploaded?.(stmt);
    } catch (error) { toast(error?.message || t('ap.surface.bankReadError', 'Kunne ikke lese bankutskriften.'), 'error'); }
    finally { setBusy(false); dragDepth.current = 0; setDragging(false); }
  };
  if (children) return <div data-bank-statement-drop style={{ position: 'relative' }}
    onDragOver={(event) => { if (!event.defaultPrevented && Array.from(event.dataTransfer?.types || []).includes('Files')) { event.preventDefault(); setDragging(true); } else setDragging(false); }}
    onDropCapture={() => setDragging(false)}
    onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
    onDrop={(event) => { setDragging(false); if (event.defaultPrevented) return; const file = event.dataTransfer?.files?.[0]; if (!file) return; event.preventDefault(); void upload(file); }}>
    {children}
    {(dragging || busy) && <div className="bank-inbox-drop" style={{ pointerEvents: 'none', zIndex: 5 }}><b>{t(busy ? 'bank.readingStatement' : 'bank.periodImport.dropFile')}</b></div>}
  </div>;
  return <div className="ap-month-control-upload">
    <label className={'bank-drop' + (dragging ? ' over' : '')}
      onDragOver={(event) => event.preventDefault()}
      onDragEnter={(event) => { event.preventDefault(); dragDepth.current += 1; setDragging(true); }}
      onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) setDragging(false); }}
      onDrop={(event) => { event.preventDefault(); void upload(event.dataTransfer?.files?.[0]); }}>
      <input type="file" accept=".csv,.pdf,.xlsx" style={{ display: 'none' }} disabled={busy}
        onChange={(event) => { void upload(event.target.files && event.target.files[0]); event.target.value = ''; }} />
      <img src={(window.__resources && window.__resources.superPlummaSafe) || 'src/img/SuperPlummaSafe.png'} alt="" style={{ width: 150, height: 'auto', display: 'block' }} />
      <h3 className="t-h2" style={{ marginTop: 8 }}>{t('bank.periodImport.missingMonth', { period: periodLabel || '' })}</h3>
      <p className="muted" style={{ maxWidth: 440, fontWeight: 600 }}>
        {t('bank.periodImport.monthUploadHelp', { account: accountLabel || t('bank.bankAccount') })}
      </p>
      <span className="btn btn-secondary mt-12"><Icon name="upload" size={17} />{t(busy ? 'bank.readingStatement' : 'bank.periodImport.title')}</span>
    </label>
  </div>;
}

// Bankavstemmingen over AI-siden. Bokføringen skjer her inne, i den samme
// flyten som på Bank-siden — ikke på en samleknapp brukeren ikke ser
// grunnlaget for.
function ApBankSurfaceModal({ month, year, task, onClose, onChanged }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickedId, setPickedId] = useState('');
  const [missingAccountsOpen, setMissingAccountsOpen] = useState(false);
  const investmentCount = backend?.appState?.investmentRegister ? window.PlummeInvestments.periodPending(backend.appState, year, month) : 0;
  const model = useMemo(
    () => window.PlummeBackend?.getBankPageModel?.() || { accounts: [], statements: [], inbox: [], postings: [] },
    [backend?.appState, backend?.activeBusinessId, refreshKey],
  );
  const inboxModel = useMemo(
    () => window.PlummeBackend?.getReceiptInboxModel?.() || { active: [] },
    [backend?.appState, backend?.activeBusinessId, refreshKey],
  );
  const receiptInMonth = (entry) => {
    const documentDate = entry?.date || entry?.receiptAnalysis?.receiptDate;
    return Boolean(documentDate && window.apPeriodIn?.(documentDate, year, month));
  };
  // Samme regel som lista under fanen: koblede bilag hoerer til banklinjen sin,
  // men et koblet bilag som trenger kontroll skal telles og vises her.
  const inboxScope = (inboxModel.active || []).filter((entry) => !entry.bankLinkedResolved);
  const unbankedInbox = inboxScope.filter(receiptInMonth);
  // Bilag i innboksen som ikke hoerer til denne maaneden — eller som aldri fikk
  // lest en dato. Fanen sier hvor mange de er, slik at «0 bilag» ikke leses som
  // «innboksen er tom» naar det ligger tre der inne. Selve lista har et valg for
  // aa vise dem.
  const outsideMonth = inboxScope.length - unbankedInbox.length;
  const configuredAccounts = (model.accounts || []).filter((account) => account.source !== 'private' && account._backend?.reconcile !== false);
  const accounts = configuredAccounts.filter((account) => window.apBankAccountInMonthScope?.(account, year, month) !== false);
  const labelFor = (statement) => statement?.mode === 'privat'
    ? 'Privatkonto'
    : (configuredAccounts.find((account) => account.id === statement?.bankAccountId)?.label || 'Driftskonto');
  const businessStatements = (model.statements || []).filter((statement) => statement.mode !== 'privat' && statement.mode !== 'private');
  const configuredAccountIds = new Set(configuredAccounts.map((account) => String(account.id || '')).filter(Boolean));
  const visibleAccountIds = new Set(accounts.map((account) => String(account.id || '')).filter(Boolean));
  const inMonth = businessStatements.filter((statement) => apStatementInMonth(statement, month, year))
    .filter((statement) => {
      const accountId = String(statement.bankAccountId || '');
      return !accountId || !configuredAccountIds.has(accountId) || visibleAccountIds.has(accountId);
    });
  // Månedskontroll viser bare utskrifter som dekker valgt måned. En fallback
  // til alle aktive utskrifter gjorde at forrige måned ble en ekstra kontofane.
  const candidates = inMonth;
  // Fanene er kontoene, ikke utskriftene. En konto uten utskrift for maaneden er
  // nettopp det blokkeringen handler om — da skal fanen staa der og tilby
  // opplasting, ikke forsvinne til brukeren har vaert innom Bank-siden.
  const accountTabs = accounts.map((account) => {
    const statement = candidates.find((entry) => entry.bankAccountId === account.id) || null;
    return {
      id: `account:${account.id}`,
      accountId: account.id,
      statement,
      label: account.label || 'Driftskonto',
      meta: statement ? (statement.period || statement.month || '') : 'Mangler bankutskrift',
    };
  });
  const missingAccounts = accountTabs.filter((entry) => !entry.statement);
  const claimedStatementIds = new Set(accountTabs.map((entry) => entry.statement?.id).filter(Boolean));
  // Utskrifter uten bankkonto (eldre importer) beholder fanen sin.
  const looseTabs = candidates.filter((statement) => !claimedStatementIds.has(statement.id)).map((statement) => ({
    id: `statement:${statement.id}`,
    accountId: statement.bankAccountId || '',
    statement,
    label: labelFor(statement),
    meta: statement.period || statement.month || '',
  }));
  const tabs = [
    ...accountTabs,
    ...looseTabs,
    ...(backend?.appState?.investmentRegister || (backend?.appState?.settings?.country === 'NO' && backend?.appState?.settings?.companyTypeCode === 'AS') ? [{ id: 'investments', label: t('investments.title'), meta: investmentCount ? String(investmentCount) : '' }] : []),
    { id: 'unbanked', label: t('nav.inbox', 'Innboks'), meta: t('ap.surface.voucherCount', '{{count}} bilag', { count: unbankedInbox.length }) + (outsideMonth ? ` · ${t('ap.surface.outsideMonth', '{{count}} utenfor måneden', { count: outsideMonth })}` : '') },
  ];
  const preferredMissingAccountId = String((Array.isArray(task?.missingAccountIds) ? task.missingAccountIds : [])[0] || '');
  const tabIds = tabs.map((entry) => entry.id).join('|');
  useEffect(() => {
    if (pickedId && tabs.some((entry) => entry.id === pickedId)) return;
    const preferred = preferredMissingAccountId ? tabs.find((entry) => String(entry.accountId || '') === preferredMissingAccountId) : null;
    const next = preferred || tabs[0] || null;
    if (next?.id) setPickedId(next.id);
  }, [pickedId, preferredMissingAccountId, tabIds]);
  const activeTab = tabs.find((entry) => entry.id === pickedId) || tabs[0] || null;
  const view = activeTab?.id === 'investments' ? 'investments' : activeTab?.id === 'unbanked' ? 'unbanked' : 'bank';
  const stmt = activeTab?.statement || null;
  // Modellen er hentet på nytt, men AI-siden har fortsatt sine gamle tall.
  // `changed` sørger for at køen lastes om når modalen lukkes, ikke ved hvert
  // eneste klikk inne i avstemmingen.
  const [changed, setChanged] = useState(false);
  const refresh = () => { setChanged(true); setRefreshKey((value) => value + 1); };
  const close = () => { if (changed && onChanged) void onChanged(); onClose(); };
  useEffect(() => {
    const markChanged = () => setChanged(true);
    window.addEventListener('plumme-inbox-changed', markChanged);
    return () => window.removeEventListener('plumme-inbox-changed', markChanged);
  }, []);
  const routeAway = (route) => { close(); if (route && window.plummeNav) window.plummeNav(route); };
  const Page = window.InnboksPage;
  const now = new Date();
  const periodStillOpen = Number(year) > now.getFullYear()
    || (Number(year) === now.getFullYear() && Number(month) >= now.getMonth());
  const periodBlockedReason = periodStillOpen
    ? t('ap.monthControl.liveBlockedReason')
    : '';
  const receiptAddress = window.PlummeBackend?.appState?.settings?.inboundReceiptEmail
    || window.PlummeBackend?.appState?.settings?.receiptInboxEmail || '';
  const blocking = stmt && window.bankEarlierUnsettled ? window.bankEarlierUnsettled(model.statements, stmt) : null;
  const ledgerBalanceIssue = stmt ? window.PlummeBankPeriodControl.statementBalanceIssue(model.bankControl, stmt) : null;
  const ledgerBlockedReason = ledgerBalanceIssue ? t(window.PlummeBankPeriodControl.balanceIssueTranslationKey(ledgerBalanceIssue), { month: stmt.month, booked: window.PlummeI18n.number(ledgerBalanceIssue.booked, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), bank: window.PlummeI18n.number(ledgerBalanceIssue.bank, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), difference: window.PlummeI18n.number(ledgerBalanceIssue.difference, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }) : '';
  const finalizeBlockedReason = periodBlockedReason || (investmentCount ? t('investments.monthPending') : '') || ledgerBlockedReason;
  const controlFindings = task?.constituentTasks?.find((entry) => entry.id === 'kontroll')?.lines || [];
  return <ApSurfaceModal title={t('ap.monthControl.label')} max={1920} className="ap-month-control-modal" onClose={missingAccountsOpen ? () => setMissingAccountsOpen(false) : close}
    subtitle={activeTab?.label || ''}
    footer={<Button variant="ghost" onClick={close}>{t('common.close')}</Button>}>
    {missingAccounts.length > 0 && <button type="button" className="ap-missing-statements" aria-haspopup="dialog" onClick={() => setMissingAccountsOpen(true)}>
      <Icon name="alertTri" size={15}/>{t('ap.review.missingStatements')}
    </button>}
    <Modal open={missingAccountsOpen} portal max={520} title={t('ap.review.missingStatements')} onClose={() => setMissingAccountsOpen(false)}>
      <div className="ap-missing-statements-list" role="dialog" aria-label={t('ap.review.missingStatements')}>
        <p className="t-sm muted" style={{ margin: '0 0 12px' }}>{t('ap.review.missingStatementsPeriod', { period: window.PlummeI18n.date(new Date(year, month, 1), { month: 'long', year: 'numeric' }) })}</p>
        <div className="col gap-10">{missingAccounts.map((entry) => <button key={entry.id} type="button" className="ap-surface-pick" onClick={() => { setPickedId(entry.id); setMissingAccountsOpen(false); }}>
          <Icon name="bank" size={18}/><span className="grow">{entry.label}</span><span className="t-sm">{t('bank.periodImport.title')}</span><Icon name="chevRight" size={16}/>
        </button>)}</div>
      </div>
    </Modal>
    <div className="ap-month-control-tabs" role="tablist" aria-label={t('ap.surface.accountsAndVouchers')}>
      {tabs.map((entry) => <button key={entry.id} type="button" role="tab"
        aria-selected={entry.id === activeTab?.id}
        className={(entry.id === activeTab?.id ? 'on' : '') + (entry.accountId && !entry.statement ? ' is-missing' : '')}
        onClick={() => setPickedId(entry.id)}>
        <span>{entry.label}</span>{entry.meta && <small>{entry.meta}</small>}
      </button>)}
    </div>
    {view === 'bank' && periodBlockedReason && <div className="ap-period-warn ap-month-control-live">
      <Icon name="clock" size={18}/><div><b>{t('ap.monthControl.liveTitle')}</b><div className="ap-period-warn-sub">{periodBlockedReason}</div></div>
    </div>}
    {view === 'investments' ? <window.InvestmentsPage embedded periodYear={year} periodMonth={month} onChanged={refresh} /> : view === 'unbanked'
      ? Page ? <Page embedded route="innboks" setRoute={routeAway} year={year} periodMonth={month} periodOnly hideBankLinkedReceipts /> : null
      : stmt && apStatementSettled(stmt)
      ? <ApBankDoneSurface stmt={stmt} acctLabel={labelFor(stmt)} onReopen={async () => {
          try {
            await window.PlummeBackend.reopenBankStatement?.(stmt.id);
            refresh();
          } catch (error) { toast(error?.message || 'Kunne ikke gjenapne avstemmingen.', 'error'); }
        }} />
      : stmt && window.BankReconcileMode
      ? <ApBankStatementUploadSurface accountId={activeTab?.accountId || ''} onUploaded={refresh}><window.BankReconcileMode stmt={stmt} acctLabel={labelFor(stmt)} statements={model.statements}
          blockingStatement={blocking} inbox={model.inbox} postings={model.postings} receiptAddress={receiptAddress}
          presentation="autopilot" finalizeBlockedReason={finalizeBlockedReason} ledgerBalanceIssue={ledgerBalanceIssue} controlFindings={controlFindings} onOpenRoute={routeAway}
          onBack={close} onArchive={async (id) => {
            try {
              await window.PlummeBackend.archiveBankStatement(id);
              refresh();
              toast(t('r1b.surface.statementArchived'), 'success');
            } catch (error) { toast(error?.message || t('r1b.surface.archiveFailed'), 'error'); }
          }} onChanged={refresh} /></ApBankStatementUploadSurface>
      : stmt
      ? <div className="ap-surface-empty">
          <Icon name="bank" size={22} />
          <div>
            <b>{t('ap.surface.bankLoadErrorTitle', 'Avstemmingen kunne ikke lastes')}</b>
            <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 2 }}>{t('ap.surface.bankLoadErrorDetail', 'Last siden på nytt, eller åpne utskriften fra Bank.')}</div>
          </div>
        </div>
      : <ApBankStatementUploadSurface accountId={activeTab?.accountId || ''} accountLabel={activeTab?.label || ''}
          periodLabel={apMonthControlPeriodLabel(month, year)} onUploaded={() => refresh()} />}
  </ApSurfaceModal>;
}

Object.assign(window, { ApSurfaceModal, ApBankSurfaceModal, apStatementInMonth });

// Eiendelsforslagene er ikke en liste å hake av — de er én beslutning per
// bilag: kostnadsfør eller aktiver, og hvilken saldogruppe. Derfor åpnes
// registreringsmodalen fra Eiendeler, ikke hele siden, forhåndsutfylt fra
// bilaget forslaget kom fra. Modalen lagrer som ny eiendel og lukker
// kvitteringen i innboksen, så kjøpet ikke blir bokført to ganger.
const AP_ASSET_GROUP_FALLBACK = 'd';

const apAssetPrefill = (queueTask, entry) => {
  const analysis = entry?.receiptAnalysis || {};
  const gross = Number(analysis.totalGross) || 0;
  const vatRate = Number(queueTask?.proposal?.vatRate ?? analysis.vatRate ?? 25) || 0;
  const net = gross / (1 + Math.max(0, vatRate) / 100);
  return {
    name: analysis.supplierName || queueTask?.title || '',
    group: queueTask?.proposal?.assetGroup || AP_ASSET_GROUP_FALLBACK,
    receiptInboxEntryId: entry?.id || queueTask?.sourceId || '',
    entry: {
      date: analysis.receiptDate || '',
      amountNet: net ? Math.round(net * 100) / 100 : '',
      vatRate,
      supplierName: analysis.supplierName || '',
      supplierInvoiceNumber: analysis.invoiceNumber || '',
      attachments: Array.isArray(entry?.attachments) ? entry.attachments : [],
    },
  };
};

function ApAssetSurfaceModal({ task, year, onClose, onChanged }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [openId, setOpenId] = useState('');
  const [dismissedIds, setDismissedIds] = useState([]);
  const [changed, setChanged] = useState(false);
  const [savingId, setSavingId] = useState('');
  const children = (task?.queueTasks || []).filter((entry) => entry.status !== 'done' && !dismissedIds.includes(entry.id));
  const close = () => { if (changed && onChanged) void onChanged(); onClose(); };
  const inbox = useMemo(
    () => window.PlummeBackend?.getReceiptInboxModel?.() || { active: [], entries: [] },
    [backend?.appState, backend?.activeBusinessId],
  );
  const entryFor = (queueTask) => (inbox.entries || inbox.active || []).find((entry) => entry.id === queueTask?.sourceId) || null;
  const groups = useMemo(
    () => (window.readFixedAssetsModel ? window.readFixedAssetsModel(year)?.groups : null) || [],
    [backend?.appState, year],
  );
  const picked = children.find((entry) => entry.id === openId) || null;
  const markAsExpense = async (queueTask) => {
    if (!queueTask?.id || !window.PlummeBackend?.updateAutopilotTaskProposal) return;
    setSavingId(queueTask.id);
    try {
      await window.PlummeBackend.updateAutopilotTaskProposal(queueTask.id, { assetDecision: 'expense', assetGroup: '' });
      setDismissedIds((ids) => [...new Set([...ids, queueTask.id])]);
      setChanged(true);
      toast(t('ap.surface.assetMovedToExpense', 'Bilaget er flyttet ut av eiendelskontrollen. Kontroller konto og MVA i bokføringslisten.'), 'info');
    } catch (error) {
      toast(error?.message || t('ap.surface.assetSkipError', 'Kunne ikke hoppe over eiendelsregistrering.'), 'error');
    } finally { setSavingId(''); }
  };
  if (picked && window.EiendelModal) {
    return <window.EiendelModal open groups={groups} prefill={apAssetPrefill(picked, entryFor(picked))}
      onClose={() => setOpenId('')} onSaved={() => { if (onChanged) void onChanged(); onClose(); }} />;
  }
  return <ApSurfaceModal title={t('ap.surface.assetTitle', 'Mulige driftsmidler')} max={760} onClose={close}
    subtitle={t('ap.surface.assetSubtitle', '{{count}} bilag over beløpsgrensen', { count: children.length })}
    footer={<Button variant="ghost" onClick={close}>{t('common.close', 'Lukk')}</Button>}>
    {children.length === 0
      ? <div className="ap-surface-empty">
          <Icon name="check" size={22} />
          <div><b>{t('ap.surface.assetEmptyTitle', 'Ingen eiendelsforslag igjen')}</b></div>
        </div>
      : <div className="col gap-10">
        <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('ap.surface.assetChooseHelp', 'Velg om bilaget skal registreres som driftsmiddel eller sendes tilbake til vanlig bokføringskontroll. Kjøpet bokføres bare én gang.')}</div>
        {children.map((queueTask) => <div key={queueTask.id} className="ap-asset-choice"><button type="button" className="ap-surface-pick ap-asset-choice-main" onClick={() => setOpenId(queueTask.id)}>
          <span className="icon-chip"><Icon name="briefcase" size={18} /></span>
          <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
            <span className="t-h3" style={{ fontSize: 14 }}>{queueTask.title}</span>
            <span className="t-sm muted" style={{ fontWeight: 600, display: 'block' }}>{queueTask.description}</span>
          </span>
          <Icon name="chevRight" size={16} />
        </button><Button size="sm" variant="secondary" icon="x" disabled={savingId === queueTask.id} onClick={() => void markAsExpense(queueTask)} data-help={t('helpTopics.aiProposal.body')} data-help-title={t('helpTopics.aiProposal.title')}>{t('ap.surface.assetNotAsset', 'Ikke driftsmiddel')}</Button></div>)}
      </div>}
  </ApSurfaceModal>;
}

Object.assign(window, { ApAssetSurfaceModal, apAssetPrefill });
